import { useCallback, useEffect, useMemo, useState } from 'react'
import { CustomerDetailFragment, ReservedResourcePhase } from 'src/api/api.generated'
import { useHotelDate } from 'src/app/hooks/context/context.hook'
import { ReservationF, ReservedResourceF } from '../types/reservation-detail.types'
import { useUpdateReservedResourceGuests } from '../api/update-reserved-resource-guests'
import { RemoveGuestOccupancyOutput, UpdateCustomerAndGuestOccupancyInput } from '../api/update-reserved-resource-guests.types'
import { validateCheckIn } from '../tabs/reserved-resources-tab/check-in/check-in-utils'
import { GuestCheckinCheckoutAction, GuestOccupancy } from '../types/guests.types'
import { defaultGuestOccupancyStart, emptyGuestOccupancy } from '../utils/guests.utils'
import { ReservationContext } from './reservation-context'
import { GContext, RRContext } from './reservation-context.types'
import {
  addRRContextGuest,
  contextToGuestFields,
  contextToOccupancyFields,
  getContextMapFromReservation,
  getRRContextFromReservedResource,
  updateRRContextGuest,
  updateRRContextGuestFromCustomerDetail,
} from './reservation-context.utils'
import { compareDates } from 'src/utils/date'

export type ReservationContextProviderProps = React.PropsWithChildren<{ reservation: ReservationF }>

export function ReservationContextProvider({ reservation, children }: ReservationContextProviderProps) {
  const hotelDate = useHotelDate()
  const [reservationCtx, setReservationCtx] = useState<ReservationF>({} as ReservationF)
  const [rrs, setRrs] = useState<Map<string, RRContext>>(new Map())
  const [submitting, setSubmitting] = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (reservation) {
      setReservationCtx({ ...reservation })
    }
  }, [JSON.stringify(reservation)])

  /** Serialize significant "reserved resources" props, to use that as dep in useEffect */
  const reservationContextCheck = useMemo(() => {
    const check = reservationCtx.reservedResources?.map((rr) => ({
      id: rr.id,
      start: rr.start,
      end: rr.end,
      adults: rr.adults,
      children: rr.children,
      guestOccupancies: rr.guestOccupancies?.map((go, index) => ({ index: index, phase: go.phase })),
      phase: rr.phase,
    }))
    return JSON.stringify(check)
  }, [reservationCtx])

  useEffect(() => {
    if (!submitting) {
      setRrs(getContextMapFromReservation(reservationCtx, hotelDate))
    }
  }, [reservationContextCheck, hotelDate, submitting])

  const updateRr = useCallback(
    (reservedResourceId: string, rr: RRContext) => {
      if (rrs.has(reservedResourceId)) {
        const newMap = new Map(rrs)
        newMap.set(reservedResourceId, rr)
        setRrs(newMap)
      }
    },
    [rrs],
  )

  const guestOccupanciesErrorShown = useCallback(
    (reservedResourceId: string) => {
      const rr = rrs.get(reservedResourceId)
      if (rr) {
        const newRr: RRContext = { ...rr, guestOccupanciesError: undefined }
        updateRr(reservedResourceId, newRr)
      }
    },
    [rrs, updateRr],
  )

  const onGuestChange = useCallback(
    (reservedResourceId: string, guestIndex: number, guest: GuestOccupancy) => {
      setLoading(true)
      const rr = rrs.get(reservedResourceId)
      if (!rr) return
      const newRr = updateRRContextGuest(rr, reservationCtx, hotelDate, guestIndex, guest)
      if (!newRr) return
      updateRr(reservedResourceId, newRr)
      setLoading(false)
    },
    [rrs, reservationCtx, updateRr, hotelDate],
  )

  const onCustomerUpserted = useCallback(
    (reservedResourceId: string, guestIndex: number, customer: CustomerDetailFragment) => {
      setLoading(true)
      const rr = rrs.get(reservedResourceId)
      if (!rr) return
      const newRr = updateRRContextGuestFromCustomerDetail(rr, reservationCtx, hotelDate, guestIndex, customer)
      if (!newRr) return
      updateRr(reservedResourceId, newRr)
      setLoading(false)
    },
    [rrs, reservationCtx, updateRr, hotelDate],
  )

  const customerErrorShown = useCallback(
    (reservedResourceId: string, guestIndex: number) => {
      const rr = rrs.get(reservedResourceId)
      const oldGuests = rr?.guests
      const oldGuest = oldGuests?.[guestIndex]
      if (rr && oldGuests && oldGuest) {
        const newGuests = [...oldGuests]
        newGuests[guestIndex] = { ...oldGuest, customerError: undefined }
        const newRR: RRContext = { ...rr, guests: newGuests }
        updateRr(reservedResourceId, newRR)
      }
      return
    },
    [rrs, updateRr],
  )

  const { updateCustomersAndGuestOccupancies: updateGuestOccupancies, reduceGuestCount, addGuest, performGuestCheckinCheckout } = useUpdateReservedResourceGuests(reservationCtx)

  const getCustomerAndGuestOccupancyInput = (rrContext: RRContext, reservedResource: ReservedResourceF) => {
    const inputs: UpdateCustomerAndGuestOccupancyInput[] = rrContext.guests.map((rrGuest) => {
      const occupancyFields = contextToOccupancyFields(rrGuest, hotelDate, reservedResource)
      if (occupancyFields === null) return { operation: 'CLEAR', occupancyIndex: rrGuest.occupancyIndex }

      const guestFields = contextToGuestFields(rrGuest)
      if (guestFields === null) return { operation: 'CLEAR', occupancyIndex: rrGuest.occupancyIndex }

      if (!rrGuest.guestId) {
        return {
          operation: 'CREATE',
          occupancyIndex: rrGuest.occupancyIndex,
          occupancy: occupancyFields,
          guest: guestFields,
        }
      } else if (rrGuest.customerShouldUpdate) {
        return {
          operation: 'UPDATE',
          occupancyIndex: rrGuest.occupancyIndex,
          occupancy: occupancyFields,
          guest: { ...guestFields, id: rrGuest.guestId },
        }
      } else {
        return {
          operation: 'ASSIGN',
          occupancyIndex: rrGuest.occupancyIndex,
          occupancy: occupancyFields,
          guest: { ...guestFields, id: rrGuest.guestId },
        }
      }
    })
    return inputs
  }

  const submitReservedResource = useCallback(
    async (reservedResourceId: string) => {
      setLoading(true)
      const rr = rrs.get(reservedResourceId)
      if (!rr) return

      const reservedResource = reservationCtx.reservedResources?.find((rrr) => rrr.id === reservedResourceId)
      if (!reservedResource) return

      const inputs = getCustomerAndGuestOccupancyInput(rr, reservedResource)

      setSubmitting(true)
      const result = await updateGuestOccupancies(reservedResourceId, inputs)

      const guests: GContext[] = []
      for (let index = 0; index < rr.guests.length; index++) {
        let guest: GContext
        const resultGuest = result.customersAndGuestOccupancies[index]
        if (resultGuest) {
          guest = {
            comingFromWci: false,
            ...resultGuest.output,
            start: resultGuest.output.start ?? defaultGuestOccupancyStart(reservedResource.start, reservedResource.end, hotelDate),
            end: resultGuest.output.end ?? reservedResource.end,
            customerShouldUpdate: resultGuest.error !== undefined,
            occupancyIndex: resultGuest.occupancyIndex,
            customerError: resultGuest.error,
          }
        } else {
          const oldGuest = rr.guests[index]
          guest = {
            ...oldGuest,
          }
        }
        guests[index] = guest
      }

      const newRR: RRContext = {
        reservedResourceId,
        guests,
        start: rr.start,
        end: rr.end,
        group: reservationCtx.isGroup,
        hasChanged: result.error !== undefined || result.customersAndGuestOccupancies.some((g) => g.error !== undefined),
        guestOccupanciesError: result.error,
        phase: rr.phase,
      }

      updateRr(reservedResourceId, newRR)

      setSubmitting(false)
      setLoading(false)
    },
    [rrs, updateRr, updateGuestOccupancies, onGuestChange, reservationCtx, hotelDate],
  )

  const cancelChanges = useCallback(
    (reservedResourceId) => {
      if (!rrs.has(reservedResourceId)) return
      const rr = reservationCtx.reservedResources?.find((rrr) => rrr.id === reservedResourceId)
      if (!rr) return
      const newMap = new Map(rrs)
      newMap.set(reservedResourceId, getRRContextFromReservedResource(rr, hotelDate, reservationCtx))
      setRrs(newMap)
    },
    [reservationCtx, rrs, hotelDate],
  )

  const submitReduceGuestCount = useCallback(
    async (reservedResourceId: string, index: number) => {
      const rr = rrs.get(reservedResourceId)
      if (!rr) return

      setSubmitting(true)
      const result = await reduceGuestCount(reservedResourceId, index, rr.guests[index]?.occupancyIndex ?? null)
      const newReservedResource = result.reservedResource

      const newRr = newReservedResource ? getRRContextFromReservedResource(newReservedResource, hotelDate, reservationCtx) : rr

      newRr.guestOccupanciesError = result.error
      updateRr(reservedResourceId, newRr)
      setSubmitting(false)
    },
    [rrs, updateRr, reduceGuestCount, hotelDate, reservationCtx],
  )

  const submitAddGuest = useCallback(
    async (reservedResourceId: string, childId: string | null, isReplaceOfCheckoutGuest?: boolean) => {
      const rr = rrs.get(reservedResourceId)
      if (!rr) return

      const rrCtx = reservationCtx.reservedResources?.find((i) => i.id === reservedResourceId)
      let result: RemoveGuestOccupancyOutput | undefined = undefined
      setSubmitting(true)
      if (isReplaceOfCheckoutGuest && rrCtx) {
        result = { reservedResource: { ...rrCtx } }
      } else {
        result = await addGuest(reservedResourceId, childId)
      }
      let newRr: RRContext

      if (result.reservedResource) {
        if (rr.guests?.filter((g) => g.phase !== ReservedResourcePhase.CHECKOUT).length < result.reservedResource.adults + result.reservedResource.children) {
          newRr = addRRContextGuest(rr, result.reservedResource, hotelDate)
        } else {
          newRr = getRRContextFromReservedResource(result.reservedResource, hotelDate, reservationCtx)
        }
      } else {
        newRr = rr
      }
      newRr.guestOccupanciesError = result.error
      updateRr(reservedResourceId, newRr)
      setSubmitting(false)
    },
    [rrs, updateRr, reduceGuestCount, hotelDate, reservationCtx],
  )

  const checkInWarning = useMemo(() => validateCheckIn(reservationCtx, hotelDate), [reservationCtx, hotelDate])

  const performGuestCheckinCheckoutHandler = useCallback(
    async (reservedResourceId: string, guestId: string, action: GuestCheckinCheckoutAction) => {
      // success callback
      const onSuccess = (newGuestPhase: ReservedResourcePhase) => {
        // update reservation context
        const reservationContextRR = reservationCtx.reservedResources?.find((rr) => rr.id === reservedResourceId)
        if (!reservationContextRR) return

        // // the reserved resource phase turn into 'RESERVATION' and 'CHECKOUT' only when all guests have the same phase.
        // // the reserved resource phase turn into 'CHECKIN' if at least one guest is in this phase.
        let newResourcePhase = newGuestPhase
        if (newGuestPhase === 'RESERVATION' || newGuestPhase === 'CHECKOUT') {
          newResourcePhase = reservationContextRR.guestOccupancies?.filter((g) => g.guest.id !== guestId)?.every((i) => i.phase === newGuestPhase) ? newGuestPhase : reservationContextRR.phase
        }

        // Update context guest phase
        const updatedGuestOccupancies = reservationContextRR.guestOccupancies?.map((go) =>
          go.guest.id === guestId ? { ...go, phase: newGuestPhase, end: newGuestPhase === 'CHECKOUT' && compareDates(go.end, hotelDate) > 0 ? hotelDate : go.end } : go,
        )

        // Update resource phase
        const updatedResourceOccupancies = reservationContextRR.resourceOccupancies?.map((ro) => ({ ...ro, phase: newResourcePhase }))

        // Update reserved resource phase
        const updReservationContextRR: ReservedResourceF = {
          ...reservationContextRR,
          phase: newResourcePhase,
          guestOccupancies: updatedGuestOccupancies,
          resourceOccupancies: updatedResourceOccupancies,
        } as ReservedResourceF
        setReservationCtx({
          ...reservationCtx,
          reservedResources: [...(reservationCtx.reservedResources?.map((rr) => (rr.id === reservedResourceId ? { ...updReservationContextRR } : rr)) ?? [])],
        })
      }

      setLoading(true)
      setSubmitting(true)
      if (action === 'DO_CHECKOUT') {
        const rr = rrs.get(reservedResourceId)
        if (!rr) return

        const reservedResource = reservationCtx.reservedResources?.find((rrr) => rrr.id === reservedResourceId)
        if (!reservedResource) return

        const inputs = getCustomerAndGuestOccupancyInput(rr, reservedResource)?.map((i) =>
          (i.operation === 'UPDATE' || i.operation === 'ASSIGN') && i.guest.id === guestId && compareDates(i.occupancy.end, hotelDate) > 0
            ? { ...i, occupancy: { ...i.occupancy, end: hotelDate } }
            : i,
        )

        await updateGuestOccupancies(reservedResourceId, inputs)
      }
      await performGuestCheckinCheckout(reservedResourceId, guestId, action, onSuccess)
      setSubmitting(false)
      setLoading(false)
    },
    [rrs, performGuestCheckinCheckout, updateRr, reservationCtx, hotelDate],
  )

  return (
    <ReservationContext.Provider //
      value={{
        //
        reservation: reservationCtx,
        reservedResources: rrs,
        checkInWarning,
        loading,
        submitReservedResource,
        submitReduceGuestCount,
        submitAddGuest,
        guestOccupanciesErrorShown,
        onGuestChange,
        onCustomerUpserted,
        customerErrorShown,
        cancelChanges,
        performGuestCheckinCheckout: performGuestCheckinCheckoutHandler,
      }}
    >
      {children}
    </ReservationContext.Provider>
  )
}
